import {
  Shield,
  Building2,
  Star,
  GitCompareArrows,
  Share2,
  Briefcase,
  Wallet,
  ArrowLeftRight,
  MapPin,
  Home,
  Coins,
  Calendar,
  Percent,
  ExternalLink,
  FileText,
  Users,
  GraduationCap,
  Thermometer,
  BookOpen,
  ClipboardCheck,
  Globe,
  Info,
} from "lucide-react";

/* City-detail tour content, ported from the mobile "City Detail" mockup.
   Card-only steps (jobs, salary, licensing, demographics, schools) dim a
   whole section; the rest ring a specific control. The generic GuidedTour
   engine renders these; this is just the data. */
export default function getSearchDetailTourSteps(isDesktop) {
  // Licensing renders as ONE combined "Licensing & Compliance" section on
  // mobile but as SEPARATE cards on desktop (License Requirements, CEUs,
  // Special Requirements, Compact State, Important Considerations), switched
  // by CSS at 768px. Give each layout its own step(s). `isDesktop` is passed
  // in (and kept reactive) by the caller so it reflects the CURRENT viewport,
  // not the width at first mount.
  const licensingSteps = isDesktop
    ? [
        {
          cardSelector: '[data-tour="cd-lic-requirements"]',
          targetSelector: '[data-tour="cd-lic-requirements"]',
          icon: <FileText />,
          title: "License Requirements",
          text: "State licensing fees and requirements for your role.",
        },
        {
          cardSelector: '[data-tour="cd-lic-ceus"]',
          targetSelector: '[data-tour="cd-lic-ceus"]',
          icon: <BookOpen />,
          title: "CEUs",
          text: "Continuing-education hours required to keep your license.",
        },
        {
          cardSelector: '[data-tour="cd-lic-special"]',
          targetSelector: '[data-tour="cd-lic-special"]',
          icon: <ClipboardCheck />,
          title: "Special Requirements",
          text: "Any extra requirements specific to this state.",
        },
        {
          cardSelector: '[data-tour="cd-lic-compact"]',
          targetSelector: '[data-tour="cd-lic-compact"]',
          icon: <Globe />,
          title: "Compact State",
          text: "Whether this state honors the nurse-licensure compact.",
        },
        {
          cardSelector: '[data-tour="cd-lic-considerations"]',
          targetSelector: '[data-tour="cd-lic-considerations"]',
          icon: <Info />,
          title: "Important Considerations",
          text: "Key notes for compact-state nurses relocating here.",
        },
      ]
    : [
        {
          cardSelector: '[data-tour="cd-licensing"]',
          targetSelector: '[data-tour="cd-licensing"]',
          icon: <FileText />,
          title: "Licensing & Compliance",
          text: "License requirements and CEUs for your role.",
        },
      ];

  return [
    {
      cardSelector: '[data-tour="cd-crime"]',
      targetSelector: '[data-tour="cd-crime"]',
      icon: <Shield />,
      title: "Crime",
      text: "The local crime grade for this city.",
    },
    {
      cardSelector: '[data-tour="cd-hospitals"]',
      targetSelector: '[data-tour="cd-hospitals"]',
      icon: <Building2 />,
      title: "Hospitals",
      text: "How many hospitals are nearby.",
    },
    {
      cardSelector: '[data-tour="cd-hosplist"]',
      targetSelector: '[data-tour="cd-hosplist"]',
      icon: <Building2 />,
      title: "Hospitals in this city",
      text: "The hospitals in this city — tap one to open its careers site.",
    },
    {
      cardSelector: '[data-tour="cd-save"]',
      targetSelector: '[data-tour="cd-save"]',
      icon: <Star />,
      title: "Save",
      text: "Save this city to revisit or compare later.",
    },
    {
      cardSelector: '[data-tour="cd-compare"]',
      targetSelector: '[data-tour="cd-compare"]',
      icon: <GitCompareArrows />,
      title: "Compare",
      text: "Add this city to a side-by-side comparison.",
    },
    {
      cardSelector: '[data-tour="cd-share"]',
      targetSelector: '[data-tour="cd-share"]',
      icon: <Share2 />,
      title: "Share",
      text: "Share this city with someone.",
    },
    {
      cardSelector: '[data-tour="cd-jobs"]',
      targetSelector: '[data-tour="cd-jobs"]',
      icon: <Briefcase />,
      title: "Jobs for this city",
      text: "Open nursing roles here, each with real take-home pay.",
    },
    {
      cardSelector: '[data-tour="cd-salary"]',
      targetSelector: '[data-tour="cd-salary"]',
      icon: <Wallet />,
      title: "Salary & Housing",
      text: "Where your monthly pay goes and what's left over.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-aff-mode"]',
      icon: <ArrowLeftRight />,
      title: "For Sale / For Rent",
      text: "Switch the affordability view between buying and renting.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-radius"]',
      icon: <MapPin />,
      title: "Search radius",
      text: "Set how far around the city to include.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-price"]',
      icon: <Home />,
      title: "Median home price",
      text: "Adjust the home price used in the estimate.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-down"]',
      icon: <Coins />,
      title: "Down payment",
      text: "Slide to set your down-payment percentage.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-term"]',
      icon: <Calendar />,
      title: "Loan term",
      text: "Choose a 15- or 30-year mortgage.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-rate"]',
      icon: <Percent />,
      title: "Interest rate",
      text: "Enter the mortgage interest rate.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-aff-bar"]',
      icon: <Wallet />,
      title: "Mortgage vs take-home",
      text: "Shows what share of your take-home pay the mortgage uses.",
    },
    {
      cardSelector: '[data-tour="cd-afford"]',
      targetSelector: '[data-tour="cd-viewhomes"]',
      icon: <ExternalLink />,
      title: "View homes",
      text: "Browse live listings on Redfin or Zillow.",
    },
    ...licensingSteps,
    {
      cardSelector: '[data-tour="cd-demographics"]',
      targetSelector: '[data-tour="cd-demographics"]',
      icon: <Users />,
      title: "Demographics",
      text: "Population, education levels and community makeup.",
    },
    {
      cardSelector: '[data-tour="cd-schools"]',
      targetSelector: '[data-tour="cd-schools"]',
      icon: <GraduationCap />,
      title: "Nearby Schools",
      text: "Ratings and details for schools around the city.",
    },
    {
      cardSelector: '[data-tour="cd-temp"]',
      targetSelector: '[data-tour="cd-temp-graph"]',
      icon: <Thermometer />,
      title: "Annual Temperature",
      text: "Month-by-month highs and lows for this city.",
    },
  ];
}
